
import { Avatar, Select, Spin, message } from "antd";
import { CloseOutlined, InfoCircleOutlined, LinkOutlined, PhoneOutlined, ClockCircleOutlined, MessageOutlined, FileTextOutlined, UserOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import api from "../../../../../core/axios/axiosInstance";
import { changeContactStatus, updateContactTags } from "../../../../../core/api/contactApi";
import { fetchTags } from "../../../../../core/data/redux/slices/TagSlice";

const ContactDetails = ({ contact, onClose }) => {
  const dispatch        = useDispatch();
  const navigate        = useNavigate();
  const contactStatuses = useSelector((s) => s.profile.contactStatuses) || [];
  const leadStatuses    = useSelector((s) => s.profile.leadStatuses)    || [];
  const allTags         = useSelector((s) => s.tags.tags) || [];
  const userRole        = useSelector((s) => s.profile.role);

  const [crmRecord, setCrmRecord]       = useState(null);
  const [loadingCrm, setLoadingCrm]     = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingTags, setSavingTags]     = useState(false);
  const [showNameTip, setShowNameTip]   = useState(false);

  useEffect(() => {
    if (allTags.length === 0) dispatch(fetchTags());
  }, []);

  useEffect(() => {
    if (!contact?.from) return;
    setCrmRecord(null);
    const phone = contact.from;
    setLoadingCrm(true);
    api.get("/getAllContactsOrLeads/searchByPhone", { params: { phone } })
      .then((res) => {
        const results = res.data?.results || [];
        if (results.length > 0) {
          const r = results[0];
          setCrmRecord({
            type:      r.type,
            contactId: r.contactId,
            status:    r.status    || "",
            tags:      r.tags      || [],
            firstname: r.firstname || "",
            lastname:  r.lastname  || "",
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoadingCrm(false));
  }, [contact?.from]);

  const handleStatusChange = async (newStatus) => {
    if (!crmRecord) return;
    setSavingStatus(true);
    try {
      await changeContactStatus({ contact_id: crmRecord.contactId, newStatus, category: crmRecord.type });
      setCrmRecord((prev) => ({ ...prev, status: newStatus }));
      message.success("Status updated");
    } catch {
      message.error("Failed to update status");
    } finally {
      setSavingStatus(false);
    }
  };

  const handleTagsChange = async (selectedTagIds) => {
    if (!crmRecord) return;
    setSavingTags(true);
    try {
      const newTags = allTags
        .filter((t) => selectedTagIds.includes(t.tag_id))
        .map((t) => ({ tag: t.tag, emoji: t.emoji, tag_id: t.tag_id }));
      await updateContactTags({ contact_id: crmRecord.contactId, tags: newTags, category: crmRecord.type });
      setCrmRecord((prev) => ({ ...prev, tags: allTags.filter((t) => selectedTagIds.includes(t.tag_id)) }));
      message.success("Groups updated");
    } catch {
      message.error("Failed to update groups");
    } finally {
      setSavingTags(false);
    }
  };

  const currentTagIds = (crmRecord?.tags || [])
    .map((t) => t.tag_id)
    .filter((id) => allTags.some((ut) => ut.tag_id === id));

  const tagOptions = allTags.map((t) => ({
    value: t.tag_id,
    label: `${t.emoji || ""} ${t.tag}`.trim(),
  }));

  const statusOptions = crmRecord?.type === "lead"
    ? leadStatuses.map((s) => ({ value: s.value, label: s.label }))
    : contactStatuses.map((s) => ({ value: s.value, label: s.label }));

  const displayName = contact?.chatName || contact?.originalName || contact?.from || "Unknown";
  const initials = displayName.charAt(0).toUpperCase();

  const handleViewInCrm = () => {
    if (!crmRecord) return;
    const record = {
      contact_id: crmRecord.contactId,
      id:         crmRecord.contactId,
      key:        crmRecord.contactId,
      firstname:  crmRecord.firstname,
      lastname:   crmRecord.lastname,
    };
    const route = crmRecord.type === "lead"
      ? (userRole === "user" ? "/agent/leads-details" : "/leads-details")
      : (userRole === "user" ? "/agent/crm/customer-details" : "/crm/customer-details");
    navigate(route, { state: { record } });
  };

  return (
    <div className="contact-details">

      {/* ── Purple hero section ── */}
      <div className="cd-hero">
        <button className="cd-close-btn" onClick={onClose}>
          <CloseOutlined />
        </button>

        <Avatar src={contact.image} size={80} className="cd-hero-avatar">
          {initials}
        </Avatar>

        <h3 className="cd-hero-name">
          {displayName}
          {contact?.chatName && contact?.originalName && contact.chatName === contact.originalName && (
            <span style={{ position: "relative", display: "inline-flex", marginLeft: 6 }}>
              <InfoCircleOutlined
                style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", cursor: "pointer" }}
                onClick={() => setShowNameTip((v) => !v)}
              />
              {showNameTip && (
                <span className="cd-name-tip">
                  This is the contact's WhatsApp profile name
                  <span className="cd-name-tip-arrow" />
                </span>
              )}
            </span>
          )}
        </h3>

        <p className="cd-hero-phone">
          <PhoneOutlined style={{ fontSize: 11, marginRight: 4 }} />
          {contact.phone}
        </p>

        {crmRecord && (
          <button className="cd-crm-btn" onClick={handleViewInCrm}>
            <LinkOutlined style={{ marginRight: 5 }} />
            View in CRM
          </button>
        )}
      </div>

      {/* ── Scrollable body ── */}
      <div className="cd-body">

        {/* Stats row */}
        <div className="cd-stats-row">
          <div className="cd-stat">
            <MessageOutlined className="cd-stat-icon" />
            <div className="cd-stat-val">{contact.sessionMessages || 0}</div>
            <div className="cd-stat-label">Messages</div>
          </div>
          <div className="cd-stat">
            <FileTextOutlined className="cd-stat-icon" />
            <div className="cd-stat-val">{contact.templateMessages || 0}</div>
            <div className="cd-stat-label">Templates</div>
          </div>
        </div>

        {/* CRM Status */}
        <div className="cd-field">
          <div className="cd-field-label">
            <UserOutlined style={{ marginRight: 5 }} />
            {loadingCrm ? "Looking up CRM…" : crmRecord ? `${crmRecord.type === "lead" ? "Lead" : "Contact"} Status` : "CRM Status"}
          </div>
          {loadingCrm ? (
            <div className="cd-field-loading"><Spin size="small" /></div>
          ) : !crmRecord ? (
            <p className="cd-field-empty">Not saved in contacts or leads</p>
          ) : (
            <Select
              value={crmRecord.status || undefined}
              onChange={handleStatusChange}
              options={statusOptions}
              placeholder="Change status"
              size="small"
              style={{ width: "100%" }}
              loading={savingStatus}
              disabled={savingStatus}
            />
          )}
        </div>

        {/* Groups / Tags */}
        {crmRecord && (
          <div className="cd-field">
            <div className="cd-field-label">Groups</div>
            <Select
              mode="multiple"
              value={currentTagIds}
              onChange={handleTagsChange}
              options={tagOptions}
              placeholder="Add to group"
              size="small"
              style={{ width: "100%" }}
              loading={savingTags}
              disabled={savingTags || loadingCrm}
              allowClear
              maxTagCount="responsive"
            />
          </div>
        )}

        {/* Info rows */}
        {contact.originalName && (
          <div className="cd-info-row">
            <span className="cd-info-label">WA Profile Name</span>
            <span className="cd-info-value">{contact.originalName}</span>
          </div>
        )}

        <div className="cd-info-row">
          <span className="cd-info-label">
            <ClockCircleOutlined style={{ marginRight: 4 }} />Last User Message
          </span>
          <span className="cd-info-value">
            {contact.userActiveStatus || "Unknown"}
            {contact.lastIncomingTime && (
              <span className="cd-info-sub">
                {new Date(contact.lastIncomingTime).toLocaleString()}
              </span>
            )}
          </span>
        </div>

        {contact.firstUserMessage && (
          <div className="cd-info-row">
            <span className="cd-info-label">First Message</span>
            <span className="cd-info-value">{contact.firstUserMessage}</span>
          </div>
        )}

        {contact.lastMessageTime && (
          <div className="cd-info-row">
            <span className="cd-info-label">Last Message</span>
            <span className="cd-info-value">
              {new Date(contact.lastMessageTime).toLocaleString()}
            </span>
          </div>
        )}

      </div>
    </div>
  );
};

export default ContactDetails;
